import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import StudySiteHubWrapper, { StudySiteHubContentProps } from '../study-site-hub-wrapper';
import ChartsSection from './sections/chart-view';
import SiteView from './sections/site-view';
import StudyView from './sections/study-view';
import WidgetsSection from './sections/widget-section';
import { FilterUIConfigType } from '@components-clean/core/study-data-filter/study-data-filter.interfaces';
import TabStripLowDom from '@components-clean/core/tabstrip-low-dom';
import { usePageToolbar } from '@contexts-clean/page-toolbar-context';
import { useStudyDataFilterContext } from '@contexts-clean/study-data-filter-context';
import { CM2Dataset } from '@interfaces/central-monitoring';
import ActionsBar from '@pages-clean/central-monitoring/components/action-bar/action-bar';
import { IFormData, StudySiteHubContext } from '@pages-clean/central-monitoring/interfaces/context';
import { LoggedInPageProps } from '@config/routes/interfaces';

import './index.scss';

const Content = ({ studyRef, dataset, studyDashboardConfig }: StudySiteHubContentProps) => {
    const [comparisonDataset, setComparisonDataset] = useState<CM2Dataset>();
    const [formData, setFormData] = useState<IFormData>({
        studyRef,
        dataset,
        hidePanelSetup: true,
        defaultDatasetFilters: {
            dataSetFilter: studyDashboardConfig.studyDashboardConfigForStudy?.dataSetFilter,
            dataSetsCount: studyDashboardConfig?.studyDashboardConfigForStudy?.dataSetsCount,
            comparisonDataset: null,
        },
        datasetFilters: {
            dataSetFilter: studyDashboardConfig.studyDashboardConfigForStudy?.dataSetFilter,
            dataSetsCount: studyDashboardConfig?.studyDashboardConfigForStudy?.dataSetsCount,
            comparisonDataset,
        },
    });

    const { sessionId } = useStudyDataFilterContext();
    const { dispatch } = usePageToolbar();

    const studyDashboardProviderValue = useMemo(
        () => ({
            current: formData,
            setCurrent: setFormData,
            setComparisonDataset,
        }),
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
        [formData, setFormData, comparisonDataset],
    );

    const { t } = useTranslation('cm', { keyPrefix: 'studyHubPage' });

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setFormData({
            ...formData,
            studyRef,
            dataset,
            datasetFilters: {
                ...formData.datasetFilters,
                comparisonDataset,
            },
        });
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [studyRef, dataset, comparisonDataset]);

    useEffect(() => {
        let route = location.pathname;
        const queryParams = new URLSearchParams(location.search);

        if (sessionId) queryParams.append('copySessionId', sessionId);
        if (queryParams.size) route = `${location.pathname}?${queryParams.toString()}`;

        dispatch({ type: 'setPopoutRoute', popoutRoute: route });
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [sessionId]);

    return (
        <StudySiteHubContext value={studyDashboardProviderValue}>
            <div className="study-hub">
                <ActionsBar
                    studyRef={studyRef}
                    datasetId={dataset.id}
                    datasetSelector
                    filterActions={FilterUIConfigType.StudyDashboard}
                />

                <WidgetsSection />

                <TabStripLowDom
                    data={[
                        {
                            title: t('chartViewTab'),
                            content: <ChartsSection />,
                            dataTestId: 'chart-tab',
                        },
                        {
                            title: t('studyOverviewTab'),
                            content: <StudyView />,
                            dataTestId: 'study-overview-tab',
                        },
                        {
                            title: t('siteOverviewTab'),
                            content: <SiteView />,
                            dataTestId: 'site-overview-tab',
                        },
                    ]}
                />
            </div>
        </StudySiteHubContext>
    );
};

const StudyHub = ({ study, cm2Dataset }: LoggedInPageProps) => {
    return <StudySiteHubWrapper study={study} cm2Dataset={cm2Dataset} Content={Content} />;
};

export default StudyHub;
